import { MicVAD, utils } from '@ricky0123/vad-web';
import { getTime } from 'date-fns';
import { EventEmitter } from 'events';
import { makeLog } from 'shared/utils/log';
import { getBaseUrl } from 'shared/utils/sse';
import { OutboundSession } from './OutboundSession';

const log = makeLog('OutboundTranscriber');

enum OutboundTranscriberStatus {
  disconnected = 'disconnected',
  connected = 'connected',
  transcribing = 'transcribing',
}

export class OutboundTranscriber extends EventEmitter {
  socket: WebSocket | null = null;
  status: OutboundTranscriberStatus = OutboundTranscriberStatus.disconnected;
  sessionId: number | null = null;
  session: OutboundSession | null = null;
  taskId: string | null = null;
  inboundVad: MicVAD | null = null;
  outboundVad: MicVAD | null = null;
  reconnectPromise: Promise<void> = Promise.resolve();

  async connect(dialSessionId: number) {
    if (this.status !== OutboundTranscriberStatus.disconnected) {
      log.error('connect: Transcriber is already connected to a dial session.', {
        dialSessionId,
      });
      return;
    }
    const domain = getBaseUrl()?.replace('http', 'ws');
    const endpoint = `${domain}/api/v1/outbound/dial_session/${dialSessionId}/transcribe/`;
    log.debug('Connecting', { endpoint });
    await new Promise((resolve, reject) => {
      const socket = new WebSocket(endpoint);
      let opened = false;
      socket.addEventListener('open', () => {
        opened = true;
        resolve(null);
        this.handleConnect();
      });
      socket.addEventListener('error', e => {
        if (!opened) {
          reject(e);
          return;
        }
        this.handleError(e);
      });
      socket.addEventListener('message', e => {
        this.handleMessage(JSON.parse(e.data));
      });
      this.socket = socket;
    });
  }

  async switchDialTask(session: OutboundSession, taskId: string) {
    if (this.status === OutboundTranscriberStatus.disconnected) {
      log.error('switchDialTask: Transcriber is disconnected.', { taskId });
      return;
    }
    const remoteStream = session.twilioInstance?.call?.getRemoteStream();
    if (!remoteStream) {
      log.error('switchDialTask: Twilio remote stream not available.');
      return;
    }
    this.taskId = taskId;
    this.session = session;
    this.clearVads();
    this.sendMessage({ type: 'start', taskId });
    this.inboundVad = await makeVad({
      channel: 'inbound',
      stream: remoteStream,
      callback: message => this.sendMessage(message),
    });
    this.outboundVad = await makeVad({
      channel: 'outbound',
      stream: await navigator.mediaDevices.getUserMedia({ audio: true }),
      callback: message => this.sendMessage(message),
    });
    this.setStatus(OutboundTranscriberStatus.transcribing);
  }

  async endTranscription() {
    if (this.status !== OutboundTranscriberStatus.transcribing) {
      log.error('endTranscription: Not currently transcribing.', { status: this.status });
      return;
    }
    const taskId = this.taskId;
    this.taskId = null;
    this.session = null;
    this.clearVads();
    this.sendMessage({ type: 'end', taskId });
  }

  async disconnect() {
    if (this.status === OutboundTranscriberStatus.disconnected) {
      log.error('disconnect: Transcriber is already in the disconnected state.');
      return;
    }
    this.socket?.close();
    this.clearVads();
    this.setStatus(OutboundTranscriberStatus.disconnected);
  }

  private clearVads() {
    this.inboundVad?.destroy();
    this.inboundVad = null;
    this.outboundVad?.destroy();
    this.outboundVad = null;
  }

  private setStatus(status: OutboundTranscriberStatus) {
    log.debug('Changing status.', { was: this.status, is: status });
    this.status = status;
    this.emit('status', status);
  }

  private handleConnect() {
    if (this.status !== OutboundTranscriberStatus.disconnected) {
      log.error('handleConnect: Transcriber is not disconnected.');
      return;
    }
    this.setStatus(OutboundTranscriberStatus.connected);
  }

  private async handleError(e: Event) {
    log.error('Socket error.', { e });
    this.emit('error', e);
    for (;;) {
      try {
        await this.reconnectPromise;
      } catch (error) {
        log.error('Error waiting for reconnect promise.', { error });
      }
      if (this.socket?.readyState === WebSocket.OPEN) return;
      this.reconnectPromise = this.connect(this.sessionId!).then(() => {
        if (this.session && this.taskId) {
          return this.switchDialTask(this.session, this.taskId);
        }
      });
    }
  }

  private handleMessage(message: any) {
    log.debug('Handling message.', { message });
    this.emit('message', { taskId: this.taskId, message });
  }

  private sendMessage(message: any) {
    if (this.status === OutboundTranscriberStatus.disconnected) {
      log.error('sendMessage: Transcriber is not connected.', {
        status: this.status,
        message,
      });
      return;
    }
    log.debug('Sending message.', { message });
    this.socket!.send(JSON.stringify({ taskId: this.taskId, ...message }));
  }
}

async function makeVad({
  channel,
  stream,
  callback,
}: {
  channel: 'inbound' | 'outbound';
  stream: MediaStream;
  callback: (message: any) => void;
}): Promise<MicVAD> {
  let startTime: number;
  const vad = await MicVAD.new({
    stream: stream,
    onSpeechStart: () => {
      log.debug('Speech started.', { channel });
      startTime = getTime(new Date());
    },
    onSpeechEnd: audio => {
      log.debug('Speech ended.', { channel });
      const wavBuffer = utils.encodeWAV(audio);
      const encodedAudio = utils.arrayBufferToBase64(wavBuffer);
      callback({
        type: 'data',
        channel: channel,
        audio: encodedAudio,
        startTime: startTime,
      });
    },
    redemptionFrames: 8,
    workletURL: `${process.env.CDN_URL}/vad.worklet.bundle.min.js`,
    modelURL: `${process.env.CDN_URL}/silero_vad.onnx`,
    ortConfig: (ort: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      ort.env.wasm.wasmPaths = {
        'ort-wasm-simd.wasm': `${process.env.CDN_URL}/ort-wasm-simd.wasm`,
        'ort-wasm-simd-threaded.wasm': `${process.env.CDN_URL}/ort-wasm-simd-threaded.wasm`,
        'ort-wasm.wasm': `${process.env.CDN_URL}/ort-wasm.wasm`,
        'ort-wasm-threaded.wasm': `${process.env.CDN_URL}/ort-wasm-threaded.wasm`,
      };
    },
  });
  vad.start();
  return vad;
}
